/**
 * @file capture/micMeter.ts
 * @brief Web Audio RMS meter from a MediaStream — drives the boardroom mic glow.
 *
 * @details
 * Mirrors mobile MeetingRecorder glow mapping: noise-gate raw RMS, then sqrt-curve
 * normalize to 0..100 for MicButton-style meterReactiveGlow. Cosmetic only —
 * never used for capture / VAD decisions.
 *
 * Safari: falls back to getByteTimeDomainData when float time-domain is missing.
 *
 * @author DES
 * @date 28-07-2026
 */

/** Quiet-room floor — hush stays dark (aligned with mobile boardroom gate). */
const NOISE_FLOOR = 0.0035;
/**
 * Speech ceiling for UNGAINED raw capture. Was 0.12 while capture ran through
 * Chrome's default AGC, which pumped levels well above the native raw profile.
 * Capture is now the raw profile on both paths (`captureProfile.ts` — AGC/AEC/NS
 * off), so levels sit where the device puts them: same 0.06 the native boardroom
 * meter uses for `'raw'` (MeetingRecorder.tsx `boardroomAudioLevel`). Keeping 0.12
 * would leave normal speech at ~half glow and read as a dead mic.
 */
export const RAW_SPEECH_CEILING = 0.06;

/**
 * The meter taps the same stream MediaRecorder writes, so when the gain stage is
 * in front of it the ceiling has to move with the gain — otherwise every boosted
 * room pins the glow at 100. Derived, never a second magic number.
 */
export function speechCeilingForGain(gain: number): number {
  return RAW_SPEECH_CEILING * (gain > 0 ? gain : 1);
}

export type MicMeterHandle = {
  stop: () => void;
};

/**
 * Map linear RMS (−1..1 time-domain) → 0..100 with noise gate + sqrt curve
 * (same shape as MeetingRecorder `boardroomAudioLevel`).
 */
export function normalizeMicRms(
  rms: number,
  ceiling: number = RAW_SPEECH_CEILING,
): number {
  if (!(rms > NOISE_FLOOR)) return 0;
  const usable = rms - NOISE_FLOOR;
  const span = Math.max(ceiling, NOISE_FLOOR * 2) - NOISE_FLOOR;
  return Math.max(
    0,
    Math.min(100, Math.sqrt(Math.min(usable, span) / span) * 100),
  );
}

function readRms(
  analyser: AnalyserNode,
  floatBuf: Float32Array<ArrayBuffer>,
  byteBuf: Uint8Array<ArrayBuffer>,
): number {
  if (typeof analyser.getFloatTimeDomainData === "function") {
    try {
      analyser.getFloatTimeDomainData(floatBuf);
      let sum = 0;
      for (let i = 0; i < floatBuf.length; i += 1) {
        const v = floatBuf[i] ?? 0;
        sum += v * v;
      }
      return Math.sqrt(sum / floatBuf.length);
    } catch {
      /* fall through to byte path */
    }
  }

  analyser.getByteTimeDomainData(byteBuf);
  let sum = 0;
  for (let i = 0; i < byteBuf.length; i += 1) {
    const v = ((byteBuf[i] ?? 128) - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / byteBuf.length);
}

/**
 * Attach an AnalyserNode to `stream` and write raw-normalized 0..100 levels into
 * `levelRef` each animation frame. Attack/release smoothing lives in
 * ListeningMicButton (same split as mobile meter → MicButton).
 */
export function startMicMeter(
  stream: MediaStream,
  levelRef: { current: number },
  ceiling: number = RAW_SPEECH_CEILING,
): MicMeterHandle {
  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;

  if (!AudioCtx) {
    levelRef.current = 0;
    return {
      stop: () => {
        levelRef.current = 0;
      },
    };
  }

  const ctx = new AudioCtx();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.25;
  source.connect(analyser);
  // Do not connect to destination — meter is silent / cosmetic.

  // Explicit ArrayBuffer backing — DOM Analyser typings reject ArrayBufferLike (TS 5.x).
  const floatBuf = new Float32Array(
    new ArrayBuffer(analyser.fftSize * Float32Array.BYTES_PER_ELEMENT),
  );
  const byteBuf = new Uint8Array(new ArrayBuffer(analyser.fftSize));
  let raf = 0;
  let stopped = false;

  const tick = () => {
    if (stopped) return;
    levelRef.current = normalizeMicRms(
      readRms(analyser, floatBuf, byteBuf),
      ceiling,
    );
    raf = requestAnimationFrame(tick);
  };

  void ctx.resume().then(() => {
    if (!stopped) raf = requestAnimationFrame(tick);
  });

  // Autoplay policies can leave AudioContext suspended until a gesture.
  const unlock = () => {
    void ctx.resume();
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });

  return {
    stop: () => {
      stopped = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
      void ctx.close().catch(() => undefined);
      levelRef.current = 0;
    },
  };
}
